<template>
  <v-card>
    <v-card-title class="remote-assistance__title">
      <span class="remote-assistance__title-text">Remote Assistance - {{ status }} - {{ timer }}</span>
      <div class="remote-assistance__actions">
        <v-btn
          icon
          @click="restart"
        >
          <v-icon color="info">
            mdi-restart
          </v-icon>
        </v-btn>
        <v-btn
          icon
          @click="closeAndReturn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-slider
        :value="sliderValue"
        :label="`Width: ${sliderValue}px`"
        :max="maxVideoWidth"
        :min="250"
        @input="onSliderInput"
      />
      <div
        ref="container"
        class="d-flex justify-center"
      >
        <!-- style="cursor: none" -->
        <div
          id="streaming-container"
          ref="streamingContainer"
        >
          <video
            id="streaming-video"
            ref="streamingVideo"
            autoplay
            playsinline
            :style="`width: ${videoWidth}px;`"
          />
          <div
            style="display:flex; justify-content: space-around; gap:35px; background-color: black; padding: 5px 15px"
          >
            <v-icon
              color="white"
              @click="onSystemBarActions('back')"
            >
              mdi-arrow-left-bold
            </v-icon>
            <v-icon
              small
              color="white"
              @click="onSystemBarActions('home')"
            >
              mdi-circle
            </v-icon>
            <v-icon
              small
              color="white"
              @click="onSystemBarActions('recent')"
            >
              mdi-square-rounded
            </v-icon>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
  import { throttle } from '../functions/throttle'
  import { EventQueue } from '../functions/EventQueue'

  export default {
    data () {
      return {
        dialog: false,
        peerConnection: null,
        dataChannel: null,
        status: '',
        videoElement: null,
        deviceId: null,
        debounceMouseMove: null,
        timer: '',
        timerInstance: undefined,
        videoWidth: 300,
        sliderValue: 300,
        desiredWidth: 300,
        maxVideoWidth: 1000,
        isAdjustingFromResize: false,
        sliderDebounceTimer: null,
        swipeListener: null,
        eventQueue: null,
        resizeObserver: null,
        aspectRatio: null,
        hasSetInitialSize: false,
        isClosing: false,
        heartbeatInterval: null,
        missedPongs: 0,
      }
    },

    created () {
      this.deviceId = this.$route.params.id

      this.$signal.getConnection(() => {
        this.restart()
      })

      this.$signalHub.$on('RemoteAssistanceAnswer', this.onRemoteAnswer)
      this.$signalHub.$on('RemoteAssistanceCandidate', this.onRemoteCandidate)
    },
    mounted () {
      this.initResizeObserver()
    },
    beforeDestroy () {
      this.$signalHub.$off('RemoteAssistanceAnswer', this.onRemoteAnswer)
      this.$signalHub.$off('RemoteAssistanceCandidate', this.onRemoteCandidate)

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      window.removeEventListener('resize', this.onWindowResize)

      this.initiateCloseSession()
    },
    methods: {
      async closeAndReturn () {
        await this.initiateCloseSession()
        this.$router.push('/devices')
      },
      restart () {
        this.clearWebRTCConnection()
        setTimeout(() => {
          this.startWebRTCConnection()
        }, 1000)
      },

      async startWebRTCConnection () {
        this.status = 'initiating'
        this.eventQueue = new EventQueue(this.processQueueEvent.bind(this))

        this.videoElement = this.$refs.streamingVideo
        this.setupEventListeners()

        this.peerConnection = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            {
              urls: 'turn:170.64.173.147:3478',
              username: 'softclient1',
              credential: 'softclient1',
            }],
        })

        await this.captureAudio()

        this.dataChannel = this.peerConnection.createDataChannel('control')
        this.dataChannel.onopen = () => {
          this.status = 'Data Channel Opened'
          this.startHeartbeat()
        }

        this.dataChannel.onmessage = (event) => {
          try {
            const data = event.data
            const obj = JSON.parse(data)
            switch (obj.type) {
              case 'copied':
              case 'cut_done':
                navigator.clipboard.writeText(obj.value).then(() => { }).catch(() => { })
                break
              case 'pong':
                this.missedPongs = 0
                break
              case 'close_session':
                console.log('Received close session event from peer. Closing connection.')
                this.clearWebRTCConnection()
                this.$router.push('/devices')
                break
              default:
                break
            }
          } catch (e) {
            console.error('Error parsing message from data channel', e)
          }
        }

        this.peerConnection.addEventListener('track', this.onTrack)
        this.peerConnection.addEventListener('icecandidate', this.onIceCandidate)
        this.peerConnection.addEventListener('icegatheringstatechange', this.onIceGatheringStateChange)
        this.peerConnection.addEventListener('iceconnectionstatechange', this.onIceConnectionStateChange)
        this.peerConnection.addEventListener(
          'connectionstatechange',
          () => console.log('Connection state:', this.peerConnection.connectionState),
        )

        this.peerConnection.addEventListener(
          'signalingstatechange',
          () => console.log('Signaling:', this.peerConnection.signalingState),
        )

        this.eventQueue.enqueue({ type: 'create-offer' })
      },

      captureAudio () {
        return new Promise((resolve) => {
          navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            stream.getTracks().forEach((track) => {
              this.peerConnection.addTrack(track, stream)
            })
            resolve(true)
          }).catch(e => {
            resolve(false)
          })
        })
      },

      updateVideoLayout () {
        const video = this.$refs.streamingVideo

        if (!video) return

        const w = video.videoWidth
        const h = video.videoHeight

        if (!w || !h) return

        const isPortrait = h > w
        console.log(`w:${w}, h:${h}, portrait:${isPortrait}`)

        this.aspectRatio = w / h

        // Trigger a resize check to adjust the width immediately based on the aspect ratio
        const container = this.$refs.container
        if (container) {
          // If we haven't set the initial size yet, default to the maximum available width
          if (!this.hasSetInitialSize) {
            this.hasSetInitialSize = true

            const paddingOffset = 48
            let calculatedMax = container.clientWidth - paddingOffset
            if (this.aspectRatio) {
              const heightOffset = 280
              const maxAvailableHeight = Math.max(200, window.innerHeight - heightOffset)
              const maxWidthByHeight = maxAvailableHeight * this.aspectRatio
              calculatedMax = Math.min(calculatedMax, maxWidthByHeight)
            }
            this.desiredWidth = Math.min(1000, Math.max(250, Math.floor(calculatedMax)))
          }
          this.handleContainerResize(container.clientWidth)
        }
      },

      onTrack (event) {
        this.status = 'Processing Image'

        const stream = event.streams[0]

        if (!this.videoElement) return

        this.videoElement.srcObject = stream

        // IMPORTANT: wait for metadata
        this.videoElement.onloadedmetadata = () => {
          this.videoElement.play().catch(() => { })
          this.updateVideoLayout()
        }

        // Handle dynamic resize (WebRTC resolution change / rotation)
        this.videoElement.onresize = () => {
          this.updateVideoLayout()
        }
      },

      onIceGatheringStateChange () {
        if (this.peerConnection.iceGatheringState === 'gathering') {
          this.status = 'gathering'
        } else if (this.peerConnection.iceGatheringState === 'complete') {
          this.status = 'gathering complete'
        }
      },

      onIceConnectionStateChange () {
        if (
          this.peerConnection.iceConnectionState === 'disconnected' ||
          this.peerConnection.iceConnectionState === 'failed'
        ) {
          this.status = 'disconnected or failed'
        }
      },

      onIceCandidate (event) {
        if (!event.candidate) {
          return
        }
        this.status = 'getting ice candidates'
        this.eventQueue.enqueue({ type: 'send-candidate', data: event.candidate })
      },

      onRemoteAnswer (answer) {
        this.eventQueue.enqueue({ type: 'receive-answer', data: answer })
      },

      onRemoteCandidate (candidate) {
        this.eventQueue.enqueue({ type: 'receive-candidate', data: candidate })
      },

      async processQueueEvent (event) {
        console.log(event.type)

        switch (event.type) {
          case 'create-offer':
            await this.executeCreateOffer()
            break
          case 'send-candidate':
            await this.$signal.sendRemoteAssistanceCandidate(this.deviceId, event.data)
            break
          case 'receive-answer':
            await this.executeRemoteAnswer(event.data)
            break
          case 'receive-candidate':
            await this.executeRemoteCandidate(event.data)
            break
        }
      },

      async executeCreateOffer () {
        try {
          const offer = await this.peerConnection.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          })

          await this.peerConnection.setLocalDescription(offer)

          const offerRes = await this.$signal.sendRemoteAssistanceOffer(this.deviceId, offer)
          this.status = offerRes?.success ? 'Connection starting' : 'Device is Offline'
          if (offerRes?.success) {
            this.startCountdown(120)
          }
        } catch (e) {
          this.status = e
        }
      },

      async executeRemoteAnswer (answer) {
        await this.peerConnection.setRemoteDescription(answer)
        this.status = 'Connected'
      },

      async executeRemoteCandidate (candidate) {
        this.status = 'remote candidate'
        await this.peerConnection.addIceCandidate(candidate)
      },

      async clearWebRTCConnection () {
        this.status = 'closing'
        this.stopHeartbeat()

        if (this.eventQueue) {
          this.eventQueue.clear()
          this.eventQueue = null
        }

        if (this.dataChannel) {
          this.dataChannel.close()
          this.dataChannel = null
        }

        if (this.peerConnection) {
          this.peerConnection.removeEventListener('track', this.onTrack)
          this.peerConnection.removeEventListener('icecandidate', this.onIceCandidate)
          this.peerConnection.removeEventListener('icegatheringstatechange', this.onIceGatheringStateChange)
          this.peerConnection.removeEventListener('iceconnectionstatechange', this.onIceConnectionStateChange)

          this.peerConnection.close()
          this.peerConnection = null
        }

        if (this.videoElement) {
          const stream = this.videoElement.srcObject
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }

          this.videoElement.srcObject = null
          this.videoElement.load()
        }

        this.removeEventListener()
        this.aspectRatio = null
        this.hasSetInitialSize = false
        this.status = 'disconnected'
      },

      setupEventListeners () {
        const video = this.$refs.streamingVideo

        // MOVE (correct throttle + binding)
        this.debounceMouseMove = throttle(this.onMove, 16)
        video.addEventListener('mousemove', this.debounceMouseMove)

        // KEYBOARD
        window.addEventListener('keydown', this.onKeyDown)

        // SWIPE (NOW USE VIDEO AS REFERENCE)
        this.swipeListener = this.swipeGestureListener(video, (event) => {
          if (!this.dataChannel || this.dataChannel.readyState !== 'open') return

          // TAP
          if (event.type === 'tap') {
            this.dataChannel.send(JSON.stringify({
              type: 'tap',
              x: event.x,
              y: event.y,
            }))
            return
          }

          // SWIPE
          if (event.type === 'swipe') {
            this.dataChannel.send(JSON.stringify({
              type: 'swipe',
              startX: event.startX,
              startY: event.startY,
              endX: event.endX,
              endY: event.endY,
              direction: event.direction,
            }))
          }
        })
      },

      removeEventListener () {
        if (this.videoElement) {
          this.videoElement.removeEventListener('mousemove', this.debounceMouseMove)
        }

        window.removeEventListener('keydown', this.onKeyDown)

        if (this.swipeListener) {
          this.swipeListener.clear()
          this.swipeListener = null
        }

        this.debounceMouseMove = null
      },

      isFocused () {
        return this.videoElement != null && (document.activeElement === this.videoElement || document.activeElement.contains(this.videoElement))
      },

      onKeyDown (e) {
        if (!this.isFocused() || !this.dataChannel || this.dataChannel.readyState !== 'open') {
          return
        }

        const key = e.key.toLowerCase()

        if (e.ctrlKey && key === 'c') {
          this.dataChannel.send(JSON.stringify({ type: 'copy' }))
          return
        }

        if (e.ctrlKey && key === 'x') {
          this.dataChannel.send(JSON.stringify({ type: 'cut' }))
          return
        }

        if (e.ctrlKey && key === 'v') {
          navigator.clipboard.readText()
            .then(text => {
              this.dataChannel.send(JSON.stringify({
                type: 'paste',
                value: text,
              }))
            })
            .catch(() => {

            })

          return
        }

        this.dataChannel.send(JSON.stringify({ type: 'key', key: e.key }))
      },

      onMove (e) {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') return

        const video = this.$refs.streamingVideo
        const rect = video.getBoundingClientRect()

        const nx = (e.clientX - rect.left) / rect.width
        const ny = (e.clientY - rect.top) / rect.height

        // clamp
        if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return

        this.dataChannel.send(JSON.stringify({
          type: 'move',
          x: nx,
          y: ny,
        }))
      },
      startCountdown (time) {
        let remainingTime = time
        clearInterval(this.timerInstance)

        this.timerInstance = setInterval(() => {
          if (remainingTime <= 0) {
            clearInterval(this.timerInstance)
            this.timer = ''
            return
          }

          this.timer = `${remainingTime}`
          remainingTime--
        }, 1000)
      },
      swipeGestureListener (videoEl, listener) {
        let startX = 0
        let startY = 0
        let isDown = false

        function getPoint (e) {
          const p = e.changedTouches?.[0] || e.touches?.[0] || e

          const rect = videoEl.getBoundingClientRect()

          const x = (p.clientX - rect.left) / rect.width
          const y = (p.clientY - rect.top) / rect.height

          return {
            x: Math.max(0, Math.min(1, x)),
            y: Math.max(0, Math.min(1, y)),
          }
        }

        function start (e) {
          isDown = true
          const p = getPoint(e)
          startX = p.x
          startY = p.y
        }

        function end (e) {
          if (!isDown) return
          isDown = false

          const p = getPoint(e)

          const endX = p.x
          const endY = p.y

          const diffX = endX - startX
          const diffY = endY - startY

          const threshold = 0.03

          // TAP
          if (Math.abs(diffX) < threshold && Math.abs(diffY) < threshold) {
            listener({
              type: 'tap',
              x: endX,
              y: endY,
            })
            return
          }

          // SWIPE
          let direction

          if (Math.abs(diffX) > Math.abs(diffY)) {
            direction = diffX > 0 ? 'right' : 'left'
          } else {
            direction = diffY > 0 ? 'down' : 'up'
          }

          listener({
            type: 'swipe',
            startX,
            startY,
            endX,
            endY,
            direction,
          })
        }

        function cancel () {
          isDown = false
        }

        videoEl.addEventListener('mousedown', start)
        videoEl.addEventListener('mouseup', end)
        videoEl.addEventListener('mouseleave', cancel)

        videoEl.addEventListener('touchstart', start, { passive: true })
        videoEl.addEventListener('touchend', end)
        videoEl.addEventListener('touchcancel', cancel)

        return {
          clear () {
            videoEl.removeEventListener('mousedown', start)
            videoEl.removeEventListener('mouseup', end)
            videoEl.removeEventListener('mouseleave', cancel)

            videoEl.removeEventListener('touchstart', start)
            videoEl.removeEventListener('touchend', end)
            videoEl.removeEventListener('touchcancel', cancel)
          },
        }
      },
      onSystemBarActions (key) {
        if (!(this.dataChannel && this.dataChannel.readyState === 'open')) {
          return
        }

        this.dataChannel.send(JSON.stringify({
          type: 'system-bar-actions',
          key,
        }))
      },
      initResizeObserver () {
        window.addEventListener('resize', this.onWindowResize)
        if (typeof ResizeObserver !== 'undefined') {
          const container = this.$refs.container
          if (container) {
            this.resizeObserver = new ResizeObserver(() => {
              this.onWindowResize()
            })
            this.resizeObserver.observe(container)
          }
        } else {
          this.onWindowResize()
        }
      },
      handleContainerResize (containerWidth) {
        const paddingOffset = 48 // pa-6 is 24px left + 24px right
        let calculatedMax = containerWidth - paddingOffset

        if (this.aspectRatio) {
          const heightOffset = 280 // height of headers, slider, controls, margins
          const maxAvailableHeight = Math.max(200, window.innerHeight - heightOffset)
          const maxWidthByHeight = maxAvailableHeight * this.aspectRatio
          calculatedMax = Math.min(calculatedMax, maxWidthByHeight)
        }

        this.maxVideoWidth = Math.min(1000, Math.max(250, Math.floor(calculatedMax)))

        const targetWidth = Math.min(this.desiredWidth, this.maxVideoWidth)

        if (this.sliderValue !== targetWidth) {
          this.sliderValue = targetWidth
        }
        if (this.videoWidth !== targetWidth) {
          this.videoWidth = targetWidth
        }
      },
      onWindowResize () {
        const container = this.$refs.container
        if (container) {
          this.handleContainerResize(container.clientWidth)
        }
      },
      onSliderInput (val) {
        this.desiredWidth = val
        this.sliderValue = val
        clearTimeout(this.sliderDebounceTimer)
        this.sliderDebounceTimer = setTimeout(() => {
          this.videoWidth = val
        }, 2000)
      },
      startHeartbeat () {
        this.stopHeartbeat()
        this.missedPongs = 0
        this.heartbeatInterval = setInterval(() => {
          if (this.missedPongs >= 3) {
            console.warn('Ping heartbeat failed, considering connection dead.')
            this.initiateCloseSession()
            return
          }

          if (this.dataChannel && this.dataChannel.readyState === 'open') {
            this.missedPongs++
            try {
              this.dataChannel.send(JSON.stringify({ type: 'ping' }))
            } catch (e) {
              console.error('Failed to send ping', e)
            }
          }
        }, 5000)
      },
      stopHeartbeat () {
        if (this.heartbeatInterval) {
          clearInterval(this.heartbeatInterval)
          this.heartbeatInterval = null
        }
        this.missedPongs = 0
      },
      async initiateCloseSession () {
        if (this.isClosing) return
        this.isClosing = true
        this.stopHeartbeat()
        this.status = 'closing'

        if (this.dataChannel && this.dataChannel.readyState === 'open') {
          try {
            this.dataChannel.send(JSON.stringify({ type: 'close_session' }))
          } catch (e) {
            console.error('Failed to send close_session event', e)
          }
        }

        await new Promise(resolve => setTimeout(resolve, 3000))
        await this.clearWebRTCConnection()
        this.isClosing = false
      },
    },
  }
</script>

<style scoped>
.remote-assistance__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 88px;
  flex-wrap: wrap;
}

.remote-assistance__title-text {
  min-width: 0;
}

.remote-assistance__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

#streaming-container {
  max-width: 100%;
  box-sizing: border-box;
}

#streaming-video {
  max-width: 100%;
  display: block;
}

@media (max-width: 959px) {
  .remote-assistance__title {
    padding-right: 72px;
  }
}
</style>
